import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { useRegionOptions } from '@/features/regions';
import { ApiError } from '@/shared/api/api-error';
import { SearchableSelect, type SearchableOption } from '@/shared/components/searchable-select';

import { emptyToNull } from '../schemas/brand.schema';
import { denominationSchema, type DenominationFormValues } from '../schemas/denomination.schema';
import { useCreateDenomination, useUpdateDenomination } from '../api/brands.queries';
import type { BrandDenomination } from '../types/brand.types';

/**
 * Create / edit a denomination. Mount it only while open (the caller does).
 *
 * Once a denomination has been sold its region and currency are fixed — a
 * different card is a new denomination — so those fields are read-only here,
 * as the Admin API also enforces (DENOMINATION_IDENTITY_LOCKED).
 */
export function DenominationFormDialog({
  brandId,
  brandName,
  denomination,
  duplicateOf,
  defaultRegionId,
  onClose,
}: {
  brandId: number;
  brandName: string;
  /** Omit for create mode. */
  denomination?: BrandDenomination;
  /**
   * Create mode, pre-filled from this denomination for another region. Provider
   * mappings are not copied: another region usually means another provider product.
   */
  duplicateOf?: BrandDenomination;
  /** Pre-selected region for a new denomination. */
  defaultRegionId?: number;
  onClose: () => void;
}) {
  const regions = useRegionOptions();
  const create = useCreateDenomination(brandId);
  const update = useUpdateDenomination(brandId);
  const mutation = denomination ? update : create;
  const isEdit = denomination !== undefined;
  const identityLocked = denomination?.hasSales ?? false;

  const form = useForm<DenominationFormValues>({
    resolver: zodResolver(denominationSchema),
    defaultValues: {
      name: (denomination ?? duplicateOf)?.name ?? '',
      arName: (denomination ?? duplicateOf)?.arName ?? '',
      // A duplicate must go to another region: none is pre-selected.
      regionId: denomination?.regionId ?? (duplicateOf ? 0 : (defaultRegionId ?? 0)),
      currencyCode: (denomination ?? duplicateOf)?.currencyCode ?? 'USD',
      imageUrl: (denomination ?? duplicateOf)?.imageUrl ?? '',
      isActive: false,
    },
  });

  // Business errors (a name in use, a locked identity) come back on their field.
  const { error } = mutation;
  useEffect(() => {
    if (!(error instanceof ApiError) || !error.fieldErrors) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      const key = (field.charAt(0).toLowerCase() + field.slice(1)) as keyof DenominationFormValues;
      if (key in form.getValues()) form.setError(key, { type: 'server', message });
    }
  }, [error, form]);

  const onSubmit = (values: DenominationFormValues) => {
    const payload = {
      name: values.name.trim(),
      arName: emptyToNull(values.arName),
      regionId: values.regionId,
      currencyCode: values.currencyCode.trim(),
      imageUrl: emptyToNull(values.imageUrl),
    };
    if (duplicateOf && values.regionId === duplicateOf.regionId) {
      form.setError('regionId', {
        type: 'validate',
        message: 'Choose a different region from the original.',
      });
      return;
    }
    if (denomination) {
      update.mutate({ denominationId: denomination.id, payload }, { onSuccess: onClose });
    } else {
      create.mutate({ ...payload, isActive: values.isActive }, { onSuccess: onClose });
    }
  };

  const isPending = mutation.isPending;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base">
            {isEdit
              ? `Edit ${denomination.name}`
              : duplicateOf
                ? `Duplicate ${duplicateOf.name} to another region`
                : `Add a ${brandName} denomination`}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {isEdit
              ? 'Availability per country and status are changed from the denominations table.'
              : duplicateOf
                ? 'A copy of its details for another region. It starts inactive, not offered anywhere and without providers — map the provider product for that region, then offer it.'
                : 'New denominations start inactive so they can be mapped to a provider and offered first.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            <div className="grid items-start gap-5 sm:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="$10" autoFocus disabled={isPending} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="arName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Arabic name</FormLabel>
                    <FormControl>
                      <Input {...field} dir="rtl" disabled={isPending} />
                    </FormControl>
                    <FormDescription>Optional.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="regionId"
                render={({ field }) => {
                  // A duplicate must go to another region, so its original is
                  // listed (for orientation) but cannot be picked.
                  const options: SearchableOption[] = (regions.data ?? []).map((region) => {
                    const isOriginal = duplicateOf?.regionId === region.id;
                    return {
                      value: String(region.id),
                      label: region.name,
                      description: region.code,
                      disabled: isOriginal,
                      hint: isOriginal
                        ? { text: 'Original', variant: 'info' }
                        : region.isActive
                          ? undefined
                          : { text: 'Inactive', variant: 'neutral' },
                    };
                  });

                  return (
                    <FormItem>
                      <FormLabel htmlFor="denomination-region">Region (store)</FormLabel>
                      <SearchableSelect
                        id="denomination-region"
                        label="Region"
                        value={field.value ? String(field.value) : null}
                        options={options}
                        onChange={(value) => field.onChange(Number(value))}
                        placeholder="Select a region"
                        searchPlaceholder="Search by name or code…"
                        emptyText="No region matches."
                        disabled={isPending || identityLocked || regions.isLoading}
                        loading={regions.isLoading}
                        triggerClassName="h-9 text-sm"
                      />
                      <FormDescription>
                        {identityLocked
                          ? 'Fixed: this denomination has been sold.'
                          : 'The store the card is valid in, e.g. US.'}
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="currencyCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Currency</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onChange={(event) => field.onChange(event.target.value.toUpperCase())}
                        maxLength={3}
                        className="font-mono text-xs uppercase"
                        disabled={isPending || identityLocked}
                      />
                    </FormControl>
                    <FormDescription>
                      {identityLocked
                        ? 'Fixed: this denomination has been sold.'
                        : 'The card’s currency, e.g. USD.'}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="imageUrl"
                render={({ field }) => (
                  <FormItem className="sm:col-span-2">
                    <FormLabel>Image URL</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="https://…" disabled={isPending} />
                    </FormControl>
                    {isEdit && (
                      <FormDescription>Leave empty to keep the current image.</FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {!isEdit && (
                <FormField
                  control={form.control}
                  name="isActive"
                  render={({ field }) => (
                    <FormItem className="border-border flex flex-row items-center justify-between rounded-lg border p-3 sm:col-span-2">
                      <div className="space-y-0.5">
                        <FormLabel>Active on creation</FormLabel>
                        <FormDescription>
                          Customers see it only once it is also offered in their country.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch
                          checked={field.value}
                          onCheckedChange={field.onChange}
                          disabled={isPending}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
                Cancel
              </Button>
              <Button type="submit" disabled={isPending || !form.formState.isDirty}>
                {isPending && <Loader2 className="size-4 animate-spin" aria-hidden />}
                {isEdit ? 'Save changes' : 'Create denomination'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
